/**
 * O compositor da conversa (E149.F2 — GUS-149): um texto e o botão de enviar, com o que faltar
 * antes (o documento pedido, o campo apontado) vindo de quem o usa. Enquanto envia, o botão fica
 * parado; a recusa do servidor aparece pelo código, com o texto do que corrigir; no sucesso o
 * texto limpa e a tela anuncia que foi registrado.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { Button, Field, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { LIMITES_DA_CONVERSA, type RecusaDaConversa } from '@/lib/conversa_do_caso';

const t = ptBR.conversaDoCaso;

export interface CompositorProps {
  rotulo: string;
  /** O texto é obrigatório para enviar (a mensagem, o questionamento, a resposta). */
  obrigatorio: boolean;
  /** Os campos que vêm antes do texto: o documento pedido, o campo apontado, a data. */
  extras?: ReactNode;
  /** Os campos de `extras` estão completos. */
  completo?: boolean;
  nota?: string;
  botao?: string;
  /** Envia; `null` no sucesso ou o código da recusa. */
  onEnviar: (texto: string | null) => Promise<RecusaDaConversa | null>;
  onCancelar?: () => void;
  testId: string;
  /** O limite do texto: o `motivo` tem um limite menor que a mensagem. */
  maxLength?: number;
}

export function Compositor({
  rotulo,
  obrigatorio,
  extras,
  completo = true,
  nota,
  botao = t.compositor.enviar,
  onEnviar,
  onCancelar,
  testId,
  maxLength = LIMITES_DA_CONVERSA.texto,
}: CompositorProps) {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [recusa, setRecusa] = useState<RecusaDaConversa | null>(null);
  const [enviado, setEnviado] = useState(false);
  const vazio = texto.trim() === '';
  const pronto = completo && !(obrigatorio && vazio) && !enviando;

  async function enviar(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    if (!pronto) return;
    setEnviando(true);
    setRecusa(null);
    setEnviado(false);
    const resultado = await onEnviar(vazio ? null : texto.trim());
    setEnviando(false);
    setRecusa(resultado);
    if (resultado === null) {
      setTexto('');
      setEnviado(true);
    }
  }

  return (
    <form
      className="space-y-3"
      data-testid={testId}
      onSubmit={(evento) => void enviar(evento)}
      noValidate
    >
      {extras}
      <Field label={rotulo} required={obrigatorio} htmlFor={id}>
        <Textarea
          id={id}
          value={texto}
          maxLength={maxLength}
          data-testid={`${testId}-texto`}
          onChange={(e) => {
            setTexto(e.target.value);
            setEnviado(false);
          }}
        />
      </Field>
      {nota !== undefined ? <p className="text-caption text-fg-dim">{nota}</p> : null}
      {recusa !== null ? (
        <p role="alert" className="text-body-sm text-danger-700" data-testid={`${testId}-recusa`}>
          {t.recusa[recusa]}
        </p>
      ) : null}
      <p aria-live="polite" className="text-caption text-success-700">
        {enviado ? t.compositor.enviado : ''}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={!pronto} data-testid={`${testId}-enviar`}>
          {enviando ? t.compositor.enviando : botao}
        </Button>
        {onCancelar !== undefined ? (
          <Button type="button" variant="ghost" onClick={onCancelar}>
            {t.compositor.cancelar}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
